import React from "react";
import { Typography } from "@mui/material";
import { DatePicker, FormField, Select, TextField } from "@outpave/ds";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useMask } from "@react-input/mask";
import dayjs from "dayjs";
import Title from "enums/Title";
import { User } from "hooks/useAddUpdateUser";
import ConfigureAddress from "components/common/ConfigureAddress";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { Stack } from "components/layout/reusableLayouts";

interface UserFormProps {
  formik: FormikProps<User>;
  showEmailField: boolean;
  onEmailBlur?: (email: string) => void;
}

const titleOptions = Object.entries(Title)
  .filter(([key]) => key !== "Other")
  .map(([key, label]) => ({ id: key, label }));

const UserForm: React.FC<UserFormProps> = (props) => {
  const { t } = useTranslation();
  const { formik, showEmailField, onEmailBlur } = props;

  const {
    handleFormikError: handleUserError,
    handleFormikHelperText: handleUserHelperText,
  } = useFormikHelpers(formik);

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  const identifyingIdMaskRef = useMask({
    mask: "___-__-____",
    replacement: { _: /\d/ },
  });

  return (
    <Stack gap="var(--space-5)">
      {showEmailField && (
        <TextField
          fullWidth
          required
          type="email"
          label={t("Email")}
          name="email"
          value={formik.values.email ?? ""}
          onChange={formik.handleChange}
          onBlur={(e) => {
            formik.handleBlur(e);
            onEmailBlur?.(e.target.value);
          }}
          error={handleUserError("email")}
          helperText={handleUserHelperText("email")}
        />
      )}

      <TextField
        fullWidth
        required
        label={t("First Name")}
        name="firstName"
        value={formik.values.firstName ?? ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={handleUserError("firstName")}
        helperText={handleUserHelperText("firstName")}
      />

      <TextField
        fullWidth
        required
        label={t("Last Name")}
        name="lastName"
        value={formik.values.lastName ?? ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={handleUserError("lastName")}
        helperText={handleUserHelperText("lastName")}
      />

      <Select
        fullWidth
        required
        label={t("Title")}
        options={titleOptions}
        value={formik.values.title || null}
        onChange={(id) => formik.setFieldValue("title", id ?? "")}
        errorText={handleUserHelperText("title") || undefined}
      />

      <TextField
        fullWidth
        required
        ref={phoneMaskRef}
        label={t("Phone Number")}
        name="phone"
        placeholder="123-456-7890"
        leading="+1"
        value={formik.values.phone ?? ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={handleUserError("phone")}
        helperText={handleUserHelperText("phone")}
      />

      <FormField
        label={t("Date of Birth")}
        required
        error={handleUserError("birthdate")}
        helperText={handleUserHelperText("birthdate")}
      >
        <DatePicker
          value={
            formik.values.birthdate ? dayjs(formik.values.birthdate) : null
          }
          onChange={(date) =>
            formik.setFieldValue(
              "birthdate",
              date ? date.format("YYYY-MM-DD") : "",
            )
          }
          onBlur={() => formik.setFieldTouched("birthdate", true)}
          maxDate={dayjs().subtract(18, "year")}
          error={handleUserError("birthdate")}
        />
      </FormField>

      <Typography
        variant="h6"
        component="h2"
        sx={{ marginTop: "var(--space-5)" }}
      >
        {t("Tax identification")}
      </Typography>

      <TextField
        fullWidth
        required
        ref={identifyingIdMaskRef}
        label={t("Social Security Number")}
        name="identifyingId"
        value={formik.values.identifyingId ?? ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={handleUserError("identifyingId")}
        helperText={handleUserHelperText("identifyingId")}
      />

      <Typography
        variant="h6"
        component="h2"
        sx={{ marginTop: "var(--space-5)" }}
      >
        {t("Address")}
      </Typography>

      <ConfigureAddress
        formik={formik}
        parent="address"
        ignoreCountry={true}
        autoFocus={false}
      />
    </Stack>
  );
};

export default UserForm;
